// frontend/src/pages/Resumes/[id].tsx

import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Card } from '@/components/common/UI/Card'
import { Badge } from '@/components/common/UI/Badge'
import { Button } from '@/components/common/UI/Button'
import { Spinner } from '@/components/common/UI/Spinner'
import { Modal, useModal } from '@/components/common/UI/Modal'
import { ResumeScore } from '@/components/resume/ResumeCard'
import { ResumeForm } from '@/components/resume/ResumeForm'
import { useToast } from '@/components/common/UI/Toast'
import { useAuthStore } from '@/store'
import {
  formatDate,
  formatCurrency,
  getStatusColor,
  getInitialsAvatar,
  generateRandomColor
} from '@/utils/helpers'

const ResumeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [showFullDescription, setShowFullDescription] = useState(false)
  const editModal = useModal()

  const resumeId = Number(id)

  const isEmployer = user?.role === 'employer' || user?.role === 'admin'

  // Получение резюме
  const { data: resume, isLoading, error } = useQuery({
    queryKey: ['resume', resumeId],
    queryFn: async () => {
      return apiClient.get(`/v1/resumes/${resumeId}`)
    },
    enabled: !!resumeId && isAuthenticated
  })
  const isOwner = user?.id === resume?.user_id
  const { data: candidateRating } = useQuery({
    queryKey: ['reviews', 'candidate', resume?.user_id],
    queryFn: () => apiClient.get<any>(`/v1/reviews/users/${resume.user_id}`),
    enabled: !!resume?.user_id,
  })

  // Получение AI оценки (только для работодателя или владельца)
  // Генерация сопроводительного письма
  const [coverLetter, setCoverLetter] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const generateCoverLetter = async () => {
    setIsGenerating(true)
    try {
      const response = await apiClient.post(`/v1/resumes/${resumeId}/cover-letter`, {
        vacancy_id: 1 // В реальном проекте нужно выбирать вакансию
      })
      setCoverLetter(response.cover_letter)
      showToast('Письмо сгенерировано', 'success')
    } catch (error: any) {
      showToast(
        error.response?.data?.detail || 'Ошибка генерации письма',
        'error'
      )
    } finally {
      setIsGenerating(false)
    }
  }

  // Удаление резюме
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/v1/resumes/${resumeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      showToast('Резюме удалено', 'success')
      navigate('/resumes')
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.detail || 'Ошибка удаления резюме',
        'error'
      )
    }
  })

  if (!isAuthenticated) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-secondary-600">Войдите, чтобы просматривать резюме</p>
        <Button className="mt-4" onClick={() => window.location.href = '/login'}>
          Войти
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="container-custom py-12 flex justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !resume) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-error">Резюме не найдено</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/resumes')}>
          Вернуться к списку
        </Button>
      </div>
    )
  }

  // Проверка прав доступа
  if (!resume.is_public && !isOwner && !isEmployer) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-secondary-500">Это резюме скрыто</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/resumes')}>
          Вернуться к списку
        </Button>
      </div>
    )
  }

  const avatarColor = generateRandomColor()
  const initials = getInitialsAvatar(
    resume.user?.full_name || resume.user?.first_name || 'Пользователь'
  )

  return (
    <div className="container-custom py-8">
      {/* Хлебные крошки */}
      <nav className="text-sm text-secondary-500 mb-6">
        <Link to="/resumes" className="hover:text-primary-600">Резюме</Link>
        <span className="mx-2">/</span>
        <span>{resume.title || resume.position}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Основная информация */}
        <div className="lg:col-span-2 space-y-6">
          {/* Заголовок */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              {/* Аватар */}
              <div className="flex-shrink-0">
                {resume.user?.avatar_url ? (
                  <img
                    src={resume.user.avatar_url}
                    alt={resume.user.full_name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: avatarColor }}
                  >
                    {initials}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-secondary-900">
                  {resume.title || resume.position}
                </h1>
                <p className="text-lg text-secondary-600">
                  {resume.user?.full_name || resume.user?.first_name || 'Пользователь'}
                </p>
                {candidateRating?.ratings_count > 0 && <p className="mt-1 text-sm"><span className="text-amber-500">{'★'.repeat(candidateRating.stars)}{'☆'.repeat(5-candidateRating.stars)}</span> <strong>{candidateRating.average_rating}</strong> <span className="text-secondary-500">· рейтинг соискателя</span></p>}

                <div className="flex flex-wrap gap-3 mt-2 text-sm text-secondary-600">
                  {resume.city && (
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {resume.city}
                    </span>
                  )}
                  {resume.salary_from && (
                    <span className="font-medium text-secondary-900">
                      {formatCurrency(resume.salary_from, resume.salary_currency)}
                      {resume.salary_to && ` - ${formatCurrency(resume.salary_to, resume.salary_currency)}`}
                    </span>
                  )}
                  {resume.experience_level && (
                    <span>Опыт: {resume.experience_level.replace('_', ' ')}</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge className={getStatusColor(resume.is_public ? 'approved' : 'draft')}>
                    {resume.is_public ? 'Опубликовано' : 'Черновик'}
                  </Badge>
                  {resume.remote_ready && (
                    <Badge variant="success">🌐 Удаленная работа</Badge>
                  )}
                  {resume.relocation_ready && (
                    <Badge variant="info">📦 Готов к переезду</Badge>
                  )}
                </div>

                <div className="mt-3 text-xs text-secondary-400">
                  <span>Обновлено: {formatDate(resume.updated_at)}</span>
                  {resume.views_count > 0 && (
                    <span className="ml-3">👁️ {resume.views_count} просмотров</span>
                  )}
                </div>
              </div>

              {/* Действия */}
              <div className="flex flex-col gap-2">
                {isOwner && (
                  <>
                    <Button size="sm" onClick={editModal.open}>
                      Редактировать
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        if (window.confirm('Вы уверены, что хотите удалить это резюме?')) {
                          deleteMutation.mutate()
                        }
                      }}
                      isLoading={deleteMutation.isPending}
                    >
                      Удалить
                    </Button>
                  </>
                )}
                {isEmployer && !isOwner && (
                  <Button size="sm">
                    Связаться с кандидатом
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* О себе */}
          {resume.summary && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-3">О себе</h3>
              <p className="text-secondary-600 whitespace-pre-wrap">
                {showFullDescription ? resume.summary : resume.summary.slice(0, 500)}
                {resume.summary.length > 500 && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="ml-1 text-primary-600 hover:text-primary-700"
                  >
                    {showFullDescription ? 'Скрыть' : 'Читать далее'}
                  </button>
                )}
              </p>
            </Card>
          )}

          {resume.interests?.length > 0 && <Card className="p-6"><h3 className="mb-3 font-semibold text-secondary-900">Профессиональные интересы</h3><div className="flex flex-wrap gap-2">{resume.interests.map((interest: string, index: number) => <span key={`${interest}-${index}`} className="rounded-full bg-primary-50 px-3 py-1 text-sm text-primary-700">{interest}</span>)}</div></Card>}
          {resume.portfolio_links?.length > 0 && <Card className="p-6"><h3 className="mb-3 font-semibold text-secondary-900">Портфолио и проекты</h3><ul className="space-y-2">{resume.portfolio_links.map((url: string, index: number) => <li key={`${url}-${index}`}><a href={url} target="_blank" rel="noreferrer" className="break-all text-primary-700 underline">{url}</a></li>)}</ul></Card>}
          {resume.recommendations?.length > 0 && <Card className="p-6"><h3 className="mb-3 font-semibold text-secondary-900">Рекомендации</h3><div className="space-y-4">{resume.recommendations.map((recommendation: any, index: number) => <div key={index} className="border-l-2 border-primary-200 pl-4"><p className="font-medium">{recommendation.name}</p>{recommendation.position && <p className="text-sm text-secondary-600">{recommendation.position}</p>}{recommendation.comment && <p className="mt-1 text-sm text-secondary-600">{recommendation.comment}</p>}{recommendation.contact && <p className="mt-1 text-sm text-secondary-500">Контакт: {recommendation.contact}</p>}</div>)}</div></Card>}

          {/* Опыт работы */}
          {resume.experience && resume.experience.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Опыт работы</h3>
              <div className="space-y-4">
                {resume.experience.map((exp: any, idx: number) => (
                  <div key={idx} className="border-l-2 border-primary-200 pl-4">
                    <div className="flex flex-wrap items-start justify-between">
                      <div>
                        <h4 className="font-medium text-secondary-900">{exp.position}</h4>
                        <p className="text-secondary-600">{exp.company}</p>
                      </div>
                      <div className="text-sm text-secondary-400">
                        {exp.start_date} — {exp.end_date || 'по настоящее время'}
                        {exp.is_current && (
                          <span className="ml-2 text-xs text-success">(текущая)</span>
                        )}
                      </div>
                    </div>
                    {exp.description && (
                      <p className="mt-2 text-sm text-secondary-600">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Образование */}
          {resume.education && resume.education.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Образование</h3>
              <div className="space-y-3">
                {resume.education.map((edu: any, idx: number) => (
                  <div key={idx} className="flex flex-wrap items-start justify-between">
                    <div>
                      <h4 className="font-medium text-secondary-900">{edu.institution}</h4>
                      <p className="text-secondary-600">
                        {edu.degree}{edu.field && `, ${edu.field}`}
                      </p>
                    </div>
                    {edu.graduation_year && (
                      <span className="text-sm text-secondary-400">{edu.graduation_year}</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Навыки */}
          {resume.skills && resume.skills.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Навыки</h3>
              <div className="flex flex-wrap gap-2">
                {resume.skills.map((skill: string) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* Языки */}
          {resume.languages && resume.languages.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Языки</h3>
              <div className="flex flex-wrap gap-4">
                {resume.languages.map((lang: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="font-medium">{lang.lang}</span>
                    <span className="text-sm text-secondary-500">
                      {lang.level}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Боковая панель */}
        <div className="space-y-6">
          {/* AI оценка */}
          {(isEmployer || isOwner) && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">AI Оценка</h3>
              <ResumeScore resumeId={resumeId} />
            </Card>
          )}

          {/* Генерация письма (только для владельца) */}
          {isOwner && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">
                Сопроводительное письмо
              </h3>
              <Button
                className="w-full"
                onClick={generateCoverLetter}
                isLoading={isGenerating}
              >
                Сгенерировать письмо
              </Button>
              {coverLetter && (
                <div className="mt-4 p-4 bg-secondary-50 rounded-lg">
                  <p className="text-sm text-secondary-600 whitespace-pre-wrap">
                    {coverLetter}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      navigator.clipboard.writeText(coverLetter)
                      showToast('Письмо скопировано', 'success')
                    }}
                  >
                    Копировать
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* Контакты (только для работодателя) */}
          {isEmployer && !isOwner && resume.is_public && (
            <Card className="p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Контакты</h3>
              <p className="text-secondary-600">
                {resume.user?.email || 'Email не указан'}
              </p>
              {resume.user?.phone && (
                <p className="text-secondary-600 mt-1">{resume.user.phone}</p>
              )}
              <Button className="w-full mt-4">
                Связаться с кандидатом
              </Button>
            </Card>
          )}

          {/* Статистика */}
          <Card className="p-6">
            <h3 className="font-semibold text-secondary-900 mb-4">Статистика</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-secondary-500">Создано</span>
                <span>{formatDate(resume.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Обновлено</span>
                <span>{formatDate(resume.updated_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Просмотров</span>
                <span className="font-medium">{resume.views_count}</span>
              </div>
              {resume.is_public && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Статус</span>
                  <Badge className="bg-green-100 text-green-800">Публичное</Badge>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Модалка редактирования */}
      <Modal
        isOpen={editModal.isOpen}
        onClose={editModal.close}
        title="Редактирование резюме"
        size="lg"
      >
        <ResumeForm
          resume={resume}
          onSuccess={() => {
            editModal.close()
            queryClient.invalidateQueries({ queryKey: ['resume', resumeId] })
            queryClient.invalidateQueries({ queryKey: ['resumes'] })
            showToast('Резюме обновлено', 'success')
          }}
          onCancel={editModal.close}
        />
      </Modal>
    </div>
  )
}

export default ResumeDetail
